/**
 * A time range in the URL (ported from d2d-DLP; ZTA design evaluation F24,
 * F26, Phase 3).
 *
 * URL form, the one the audit page already wrote: `range=24h` is a live
 * relative window; `from` / `to` (RFC3339, or the local wall time the old
 * From / To fields wrote) are a pinned window; neither is the page's default.
 *
 */
import { parseDuration, type TimeRange } from './timeRange';

function toRfc3339(v: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** URL → time range, or `fallback` when the URL names none. */
export function readTimeRange(params: URLSearchParams, fallback: TimeRange): TimeRange {
  const rel = parseDuration(params.get('range') ?? '');
  if (rel) return rel;
  const from = toRfc3339(params.get('from'));
  const to = toRfc3339(params.get('to'));
  if (from || to) return { kind: 'absolute', from, to };
  return fallback;
}

/** Writes a range into `params` (in place) in the form readTimeRange reads. */
export function writeTimeRange(params: URLSearchParams, range: TimeRange): URLSearchParams {
  params.delete('range');
  params.delete('from');
  params.delete('to');
  if (range.kind === 'relative') params.set('range', `${range.amount}${range.unit}`);
  else if (range.kind === 'absolute') {
    if (range.from) params.set('from', range.from);
    if (range.to) params.set('to', range.to);
  }
  return params;
}
